import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";

/**
 * Modal that captures Zoom registration details (bot name, email, company,
 * optional meeting password) before sending the bot.
 *
 * Reusable for:
 *  - "Send bot" with a registration-required URL
 *  - "Retry" on a failed meeting that needs new credentials
 */
export function BotRegistrationDialog({
  open, onOpenChange, meetingType, errorCode,
  defaults, onSubmit, submitting,
}) {
  const [botName, setBotName] = useState("");
  const [botEmail, setBotEmail] = useState("");
  const [botCompany, setBotCompany] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (open) {
      setBotName(defaults?.bot_name || "Noteology AI");
      setBotEmail(defaults?.bot_email || "");
      setBotCompany(defaults?.bot_company || "");
      setPassword("");
    }
  }, [open, defaults]);

  const needsPassword = errorCode === "ZoomMeetingPasswordWrong" || errorCode === "meeting_password_incorrect";
  const isPasswordErr = errorCode === "ZoomMeetingPasswordWrong" || errorCode === "meeting_password_incorrect";
  const title = errorCode
    ? "Retry with new details"
    : (meetingType === "registration_required"
        ? "Zoom registration required"
        : "Confirm bot details");
  const description = isPasswordErr
    ? "The bot was rejected — either the password or the webinar registration token is missing. Paste the complete invite link (including everything after the ?) or enter the password below."
    : (meetingType === "registration_required"
        ? "This Zoom meeting requires registration. Noteology will register on your behalf with these details."
        : "Tell Noteology how to identify the bot when joining this Zoom meeting.");

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      bot_name: botName,
      bot_email: botEmail,
      bot_company: botCompany,
      meeting_password: password,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="bot-registration-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {errorCode && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span><span className="font-mono">{errorCode}</span> — adjust details and try again.</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="reg-bot-name">Bot display name</Label>
            <Input id="reg-bot-name" value={botName} onChange={(e) => setBotName(e.target.value)} required data-testid="reg-bot-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reg-bot-email">Bot email <span className="text-muted-foreground text-xs">(must be a real address you control)</span></Label>
            <Input id="reg-bot-email" type="email" value={botEmail} onChange={(e) => setBotEmail(e.target.value)} required data-testid="reg-bot-email-input" placeholder="notetaker@yourcompany.com" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reg-bot-company">Company <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Input id="reg-bot-company" value={botCompany} onChange={(e) => setBotCompany(e.target.value)} data-testid="reg-bot-company-input" />
          </div>
          {needsPassword && (
            <div className="space-y-1.5">
              <Label htmlFor="reg-password">Meeting password</Label>
              <Input id="reg-password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reg-password-input" />
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="reg-cancel-button">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} data-testid="reg-submit-button">
              {submitting ? "Sending…" : "Send bot"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
